import ConnectableObject from "./ConnectableObject";
import { copyAllProperties } from "./utils";
import Connection from "./Connection";
import { ConnectionType } from "./Connection";
import SceneTreeViewModel from "../modelTreeViews/SceneTreeviewModel";
import { KnxSceneInfo } from "./KnxSceneInfo";
import { ChannelType, channelTypeInfo } from "./ChannelType";
import BaseModel, { executePaste_Parameters } from "./BaseModel";
import BaseObject from "./BaseObject";
import type Consumer from "./Consumer";
import BaseModelMap from "./BaseModelMap";
import type Channel from "./Channel";
import type Device from "./Device";
import type Page from "./Page";

//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import Room from "./Room";
import Button from "./Button";
import Logger from "../Logger";
import ButtonChannel from "./ButtonChannel";
import { itx } from "../translationItx/itx";
import Actor from "./Actor";

const logger = new Logger();
ObjectFactory.registerClass("Scene", (data?: any, parent?: BaseModel) => {
  return new Scene(data, parent);
});
//////////////////////////////////////////////////
export default class Scene extends ConnectableObject {
  public get className(): string {
    return "Scene"; // no itx
  }

  getParentCollectionName(): string | undefined {
    return "scenes"; // no itx
  }

  // -----------------------------------------------------------------------------
  // knxSceneInfo
  // -----------------------------------------------------------------------------
  _knxSceneInfo: KnxSceneInfo | undefined;
  get knxSceneInfo(): KnxSceneInfo | undefined {
    return this._knxSceneInfo;
  }

  set knxSceneInfo(newValue: KnxSceneInfo | undefined) {
    if (this._knxSceneInfo === newValue) return;
    this.beforeChange("knxSceneInfo");
    this._knxSceneInfo = newValue;
    this.changed("knxSceneInfo");
  }

  // -----------------------------------------------------------------------------
  connections = new BaseModelMap<Connection>(Connection, this, "sc", false); // no itx

  // persistent properties

  public get isScene(): boolean {
    return true;
  }

  // -----------------------------------------------------------------------------
  // sceneNumber
  // -----------------------------------------------------------------------------
  _sceneNumber = 0;

  set sceneNumber(newValue: number) {
    this.beforeChange("sceneNumber");
    this._sceneNumber = newValue;

    this.changed("sceneNumber");
  }
  get sceneNumber(): number {
    return this._sceneNumber;
  }

  // -----------------------------------------------------------------------------

  getChannelType(): ChannelType {
    return ChannelType.Scene;
  }

  getIconUrl(): string | undefined {
    return "/images/various/scene.png"; // no itx
  }

  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);

    copyAllProperties(data, this);
    this.treeViewModel = new SceneTreeViewModel(this);
    this.connections.onChange.subscribe((changedObject: BaseObject, changedProperty: string | any | undefined) => {
      logger.log("scene connected or disconnected");
    });
  }

  //////////////////////////////

  getPersistentProperties(): string[] {
    return ["_sceneNumber", "knxSceneInfo", ...super.getPersistentProperties()]; // no itx
  }

  //////////////////////////////
  delete(): Array<BaseObject> {
    this.isDeleting = true;

    return super.delete();
  }

  canBeEditedInteractive(): boolean {
    return false;
  }

  getParentChildrenGroup(): Array<BaseModel> | undefined {
    return state.sceneGroup.sortBy("sceneNumber");
  }

  // -----------------------------------------------------------------------------
  // collapseMode
  // -----------------------------------------------------------------------------
  protected _collapseMode = 0; // not selected

  get collapseMode(): number {
    if (this.selected) {
      return 1;
    } else {
      return 0;
    }
  }

  // -----------------------------------------------------------------------------

  isConnectable(
    type: ConnectionType,

    partnerObject: ConnectableObject,
    checkForConnection?: Connection,
  ) {
    if (type == ConnectionType.Channel || channelTypeInfo(partnerObject.channelType).isSupply) {
      return false;
    }

    if (type == ConnectionType.Button) {
      return partnerObject.channelType == ChannelType.Scene || partnerObject.channelType == ChannelType.Generic;
    }

    if (partnerObject.className == "Scene") {
      return false;
    }

    // if (partnerObject.className == "Consumer") {
    //   // check if scene is already connected with button
    //   let res = true;
    //   this.connections.forEach((con) => {
    //     const partner = con.getPartner(this);
    //     if (partner?.className != "Consumer") {
    //       res = false;
    //     }
    //   });
    //   return res;
    // }

    return true;
  }

  get fullname(): string {
    return `${this?.name}`;
  }
  getHtmlElement() {
    return document.getElementById(this.id);
  }

  getConnectedConsumers(): Array<Consumer> {
    var res = new Array<Consumer>();
    logger.log(`getConnectedConsumers ${this.connections.size}`);
    this.connections.forEach((connection) => {
      if (connection.type === ConnectionType.Scene) {
        const p = connection.getPartner(this);
        if (p?.className == "Consumer") {
          res.push(p as Consumer);
        }
      }
    });
    return res;
  }

  getConnectedChannels(): Array<Channel> {
    const res = new Array<Channel>();

    const consumers = this.getConnectedConsumers();
    consumers.forEach((consumer) => {
      const channel = consumer.getConnectedChannel();
      if (channel) {
        res.push(channel);
      }
    });

    return res;
  }

  updateAllChannels() {
    const channels = this.getConnectedChannels();
    channels.forEach((channel) => {
      const parent = channel.parent as Device;
      parent?.updateAllFromKnxMetadata();
    });
  }

  updateOneConnection(connection: Connection) {
    const consumer = connection.getPartner(this);
    if (consumer) {
      consumer.connections.forEach((connection) => {
        if (connection.type == ConnectionType.Channel || connection.type == ConnectionType.Button) {
          const partner = connection.getPartner(consumer);
          if (partner?.isChannel) {
            (partner as Channel)?.updateParametersAfterConnectionStateChanged(connection);
          }
        }
      });
    }
  }

  onConnect(connection: Connection) {
    this.updateOneConnection(connection);
  }

  onDisconnect(connection: Connection) {
    this.updateOneConnection(connection);
  }
  connect(page: Page, consumer: Consumer) {
    var scene = this;
    const c = new Connection({}, page);

    if (this.className == "Multiplexer") {
      c.type = ConnectionType.Multiplexer;
    } else {
      c.type = ConnectionType.Scene;
    }

    /// from = consumer
    c.fromId = consumer.id;

    /// to = channel
    c.toId = scene.id;

    c.addToConnectableObjects();
    c.addToPage();

    scene.parent?.raiseOnChange(); // to update drawing in compact view

    // allow redraw, to get correct port position in collapsed mode
    setTimeout(() => {
      c.updateEndPoints();
    }, 100);
  }

  setSelected(newValue: boolean) {
    if (newValue != this.selected) {
      super.setSelected(newValue);
      // (this.parent as SceneGroup)?.updateConnectionEndpoints();
      state.sceneGroup.forEach((scene) => {
        scene.updateConnectionEndpoints();
      });
    }
  }

  async ensureAllActorKnxProductDatasAreLoaded() {
    const notYetloadedDevices = new Map<string, Device>();

    this.connections.forEach((connection) => {
      if (connection.type == ConnectionType.Scene) {
        const partner = connection.getPartner(this);
        if (partner?.className == "Consumer") {
          const channel = partner.getConnectedChannel();
          const device = channel?.parent as Device;
          if (device && device.template) {
            if (device.template.knxProductData.globalMapLoaded) {
            } else {
              notYetloadedDevices.set(device.id, device);
            }
          }
        }
      }
    });

    //
    // convert to array to enable an awaitable for loop
    // forEach is not compatible with await !!
    //
    const notYetloadedDevicesArray = new Array<Device>();
    notYetloadedDevices.forEach((device) => notYetloadedDevicesArray.push(device));

    for (let i = 0; i < notYetloadedDevicesArray.length; i++) {
      const device = notYetloadedDevicesArray[i];
      await device.updateAllFromKnxMetadata();
    }
  }

  /////////////////////////////////////////////////////////////////
  ////////// for copy
  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    if (startItem.className == "Scene" || startItem.className == "SceneGroup") {
      const res = new Array<BaseModel>();
      this.connections.forEach((connection) => {
        res.push(connection);
      });
      return res;
    }
    // scene don`t give the connection back
    return [];
  }

  // getExtendedCopyCollection(startItem:BaseModel): Array<BaseModel> {
  //   const res = new Array<BaseModel>();
  //   this.connections.forEach((connection) => {
  //     res.push(connection);
  //   });
  //   return res;
  // }

  //////////////////////////////////////////////////////
  ///////////// for paste

  updateSceneNumberInPaste(parentCollection: BaseModelMap<Scene>) {
    var sceneNumber = this.sceneNumber;

    const scenesInSceneGroup = parentCollection;
    scenesInSceneGroup.forEach((scene) => {
      if (scene.sceneNumber >= sceneNumber) {
        sceneNumber++;
      }
    });
    this.sceneNumber = sceneNumber;
  }

  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    return (await this.parent?.pasteItem(params)) || [];
  }

  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj } = params;

    if (isCopyRootObj && parent.className == "BaseModelMap") {
      this.updateNameInPaste(state.sceneGroup);
      this.updateSceneNumberInPaste(state.sceneGroup);
    }
    this.parent = parent;
    (parent as BaseModelMap<Scene>).add(this);

    // paste all Connections
    var undoObjects: Array<BaseModel> = [this];
    this.connections.forEach((connection) => {
      var partner = connection.getPartner(this);
      if (partner?.sourceId) {
        partner = this.state.objectRegistry.getObject(partner.sourceId);
      }
      var page: Page | undefined;

      if (partner?.className == "Consumer") {
        page = (partner.parent as Room)?.page;
        // } else if (partner?.className == "ButtonChannel") {
        //   page = ((partner.parent as Button)?.parent as Room)?.page;
      }
      if (page && partner) {
        connection.executePasteHelper({
          isCopyRootObj: false,
          initiator: this,
          undoObjects: undoObjects,
          page: page,
        });
        // update knx metadata of connected actors
        const actor = partner.getConnectedChannel()?.parent as Actor;
        if (actor.template) {
          actor.reEvalKnxMetadataNeeded = true;
          actor.updateAllFromKnxMetadata();
        }
      }
    });

    return undoObjects;
  }

  // scenes dont have tags and also no fulltags

  getFullTagParts(): Array<any> {
    return [];
  }

  getAllSceneNumbersWithoutOwn(): Array<any> | undefined {
    const sceneNumber = new Array<string>();
    state.sceneGroup.forEach((scene) => {
      if (scene.className == "Scene" && scene != this) {
        sceneNumber.push(scene.sceneNumber.toString());
      }
    });
    return sceneNumber;
  }
  geAllSceneNamesWithoutOwn(): string[] | undefined {
    const sceneNames = new Array<string>();
    state.sceneGroup.forEach((scene) => {
      if (scene.className == "Scene" && scene != this) {
        sceneNames.push(scene.name);
      }
    });
    return sceneNames;
  }

  getAllConnectedActorChannels() {
    const res = new Array<Channel>();

    this.connections.forEach((connection) => {
      if (connection.type == ConnectionType.Scene) {
        const partner = connection.getPartner(this);
        if (partner?.className == "Consumer") {
          const channel = (partner as Consumer).getConnectedChannel();
          if (channel) {
            res.push(channel);
          }
        }
      }
    });

    return res;
  }

  getAllConnectedButtonChannels(): Array<ButtonChannel> {
    const res = new Array<ButtonChannel>();

    this.connections.forEach((connection) => {
      if (connection.type == ConnectionType.Scene) {
        const partner = connection.getPartner(this);
        if (partner?.className == "ButtonChannel") {
          res.push(partner as ButtonChannel);
        }
      }
    });

    return res;
  }

  connectConsumer(page: Page, consumer: Consumer): Connection {
    return this.connectObject(page, consumer, ConnectionType.Scene);
  }
  canBeDeletedInteractive(): boolean {
    if (this.connections.size == 0) {
      return true;
    } else {
      return false;
    }
  }

  getDeleteConfirmText(): string | undefined {
    if (!this.canBeDeletedInteractive()) {
      const T1 = this.isMultiplexer ? this.fullTag + " " + this.name : this.sceneNumber + " " + this.name;
      let T2 = `${this.connections.size} ${itx("components.plural.Connection")}`;

      return itx("Scene.T1StillHasT2", "T1 hat noch T2.", {
        T1: `${T1}`,
        T2: T2,
      });
    } else {
      return super.getDeleteConfirmText();
    }
  }
}
